import React from 'react'
import { Text, View } from '@react-pdf/renderer'
import {
  Callout,
  ChartImage,
  Columns,
  CountTable,
  InPairs,
  DataTable,
  GOOD,
  INK,
  LINE,
  MUTED,
  NAVY,
  Pill,
  ReportPage,
  SectionHeading,
  SectionTitle,
  StatRow,
  TEAL,
  WARN,
  clean,
  fmtDateTime,
  fmtDayKey,
  fmtHour,
  fmtMinutes,
  fmtPct,
  topEntries,
} from '../../../../../../components/PDF/pdfKit'
import { categoriseComplaints } from '../complaintCategories'

const AGE_ORDER = ['0-4', '5-17', 'Under 18 (age not recorded)', '18-25', '26-50', '51-65', '66+', 'Unknown']
const ordered = (counts, order) =>
  order.filter((k) => counts?.[k]).map((k) => [k, counts[k]])

const complaintCategoryOf = (label) =>
  label === 'Other' ? '-' : Object.keys(categoriseComplaints({ [label]: 1 }))[0]

export const ClinicalActivity = ({ number, data, chrome, charts, include }) => {
  const c = data.overall.clinical
  const part = (key) => include.part('clinical', key)
  return (
    <ReportPage chrome={chrome}>
      <SectionHeading
        number={number}
        title="Clinical activity"
        intro="Every patient, first aid and welfare record linked to the event. Figures are totals only."
      />
      {part('headline') && (
        <StatRow
          items={[
            { label: 'Presentations', value: c.total },
            { label: 'Occurred at event', value: c.occurred?.Yes || 0, sub: c.occurred?.No ? `${c.occurred.No} happened elsewhere` : null },
            { label: 'Welfare cases', value: c.byMode?.Welfare || 0 },
            { label: 'Critical care cases', value: c.byMode?.['Critical Care'] || 0 },
            { label: 'Busiest hour', value: fmtHour(c.peakHour?.hour), sub: c.peakHour ? `${c.peakHour.count} presentations` : null, color: TEAL },
          ]}
        />
      )}

      {part('hourly') && (
        <>
          <SectionTitle>Presentations by hour of day</SectionTitle>
          <ChartImage image={charts.hourly} caption="All operational days combined. Hours run from 05:00." />
        </>
      )}

      <InPairs
        blocks={[
          part('complaints') && (
            <>
              <SectionTitle>Presenting categories</SectionTitle>
              <CountTable rows={topEntries(categoriseComplaints(c.complaints), 9)} total={c.total} />
            </>
          ),
          part('outcomes') && (
            <>
              <SectionTitle>Outcome</SectionTitle>
              <CountTable rows={topEntries(c.outcomes, 12)} total={c.total} />
            </>
          ),
          part('age') && (
            <>
              <SectionTitle>Age</SectionTitle>
              <CountTable rows={ordered(c.ageBands, AGE_ORDER)} total={c.total} />
            </>
          ),
          part('sex') && (
            <>
              <SectionTitle>Sex</SectionTitle>
              <CountTable rows={topEntries(c.sex)} total={c.total} />
            </>
          ),
          part('recordType') && (
            <>
              <SectionTitle>Record type</SectionTitle>
              <CountTable rows={topEntries(c.byMode)} total={c.total} />
            </>
          ),
          part('welfare') && (
            <>
              <SectionTitle>Welfare support</SectionTitle>
              <CountTable rows={topEntries(c.welfareCategories)} total={c.byMode?.Welfare || undefined} />
            </>
          ),
        ]}
      />

      {part('complaints') && (
        <>
          <SectionTitle>Most common presenting complaints</SectionTitle>
          <DataTable
            columns={[
              { label: 'Presenting complaint', flex: 3 },
              { label: 'Category', flex: 2 },
              { label: 'Count', flex: 0.8, align: 'right' },
            ]}
            rows={topEntries(c.complaints, 12).map(([label, count]) => [label, complaintCategoryOf(label), count])}
            emptyText="No presenting complaints recorded"
          />
        </>
      )}
    </ReportPage>
  )
}

const DayCad = ({ cad }) =>
  cad?.total ? (
    <DataTable
      columns={[
        { label: 'Control room', flex: 2 },
        { label: 'Median', flex: 1, align: 'right' },
        { label: '90th centile', flex: 1, align: 'right' },
      ]}
      rows={[
        ['Incidents', String(cad.total), ''],
        ['Call to first on scene', fmtMinutes(cad.intervals.callToScene.median), fmtMinutes(cad.intervals.callToScene.p90)],
        ['Time on scene', fmtMinutes(cad.intervals.onScene.median), fmtMinutes(cad.intervals.onScene.p90)],
        ['Job cycle', fmtMinutes(cad.intervals.jobCycle.median), fmtMinutes(cad.intervals.jobCycle.p90)],
      ]}
    />
  ) : null

export const DayByDay = ({ number, data, chrome, charts, include }) => {
  // Figures the planner unticked elsewhere stay out of the day pages too
  const allowed = include.allowed
  const showCad = !!data.controlRoom && allowed('controlRoom', 'headline')
  const showTreatment = !!data.treatment && allowed('treatment', 'headline')
  const showStaff = allowed('overview', 'staff')
  const showHs = allowed('healthSafety', 'concerns')
  const showFeedback = allowed('patientFeedback', 'counts')
  const showMeds = allowed('medicines', 'table')
  const individual = include.complaintView === 'individual'
  return (
    <>
      {include.part('days', 'comparison') && (
      <ReportPage chrome={chrome}>
        <SectionHeading
          number={number}
          title="Day by day"
          intro="Each operational day runs from 05:00 to 05:00 the next morning, so late-night activity counts towards the day it followed."
        />
        <ChartImage image={charts.dailyPresentations} caption="Patient presentations per operational day." />
        <SectionTitle>Comparison of days</SectionTitle>
        <DataTable
          columns={[
            { label: 'Day', flex: 1.4 },
            { label: 'Patients', flex: 0.8, align: 'right' },
            { label: 'To hospital', flex: 0.9, align: 'right' },
            { label: 'Busiest hour', flex: 0.9, align: 'right' },
            ...(showCad ? [{ label: 'CAD incidents', flex: 0.9, align: 'right' }, { label: 'Median response', flex: 1, align: 'right' }] : []),
            ...(showStaff ? [{ label: 'Staff', flex: 0.6, align: 'right' }] : []),
            ...(showHs ? [{ label: 'H&S', flex: 0.5, align: 'right' }] : []),
          ]}
          rows={data.days.map((d) => [
            fmtDayKey(d.key),
            d.clinical.total,
            d.clinical.conveyed,
            fmtHour(d.clinical.peakHour?.hour),
            ...(showCad ? [d.cad?.total ?? 0, fmtMinutes(d.cad?.intervals.callToScene.median ?? null)] : []),
            ...(showStaff ? [d.staffing.people] : []),
            ...(showHs ? [d.healthSafety] : []),
          ])}
        />
      </ReportPage>
      )}

      {include.part('days', 'pages') && data.days.map((d, i) => {
        const dayCharts = charts.days?.[d.key] || {}
        const c = d.clinical
        return (
          <ReportPage chrome={chrome} key={d.key}>
            <Text style={{ fontSize: 7, color: TEAL, fontFamily: 'Helvetica-Bold', textTransform: 'uppercase', letterSpacing: 0.8 }}>
              {i === 0 && !include.part('days', 'comparison') ? `${number}  Day by day` : 'Day by day'}
            </Text>
            <Text style={{ fontSize: 14, color: NAVY, fontFamily: 'Helvetica-Bold', marginTop: 2, marginBottom: 8 }}>
              {fmtDayKey(d.key, true)}
            </Text>
            <StatRow
              items={[
                { label: 'Presentations', value: c.total },
                { label: 'Went to hospital', value: c.conveyed, sub: c.conveyanceRate !== null ? `${c.conveyanceRate}%` : null, color: c.conveyed ? WARN : GOOD },
                { label: 'Busiest hour', value: fmtHour(c.peakHour?.hour), color: TEAL },
                ...(showStaff ? [{ label: 'Staff on duty', value: d.staffing.people, sub: `${d.staffing.plannedHours} planned hours` }] : []),
                ...(showHs ? [{ label: 'H&S concerns', value: d.healthSafety, color: d.healthSafety ? WARN : GOOD }] : []),
              ]}
            />
            {c.total ? (
              <>
                {allowed('clinical', 'hourly') && (
                  <>
                    <SectionTitle>Presentations by hour</SectionTitle>
                    <ChartImage image={dayCharts.hourly} />
                  </>
                )}
                <InPairs
                  blocks={[
                    allowed('clinical', 'complaints') && (
                      <>
                        <SectionTitle>Presenting categories</SectionTitle>
                        <CountTable rows={topEntries(categoriseComplaints(c.complaints), 8)} total={c.total} />
                      </>
                    ),
                    allowed('clinical', 'outcomes') && (
                      <>
                        <SectionTitle>Outcome</SectionTitle>
                        <CountTable rows={topEntries(c.outcomes, 8)} total={c.total} />
                      </>
                    ),
                  ]}
                />
                {individual && allowed('clinical', 'complaints') && (
                  <>
                    <SectionTitle>Presenting complaints</SectionTitle>
                    <DataTable
                      columns={[
                        { label: 'Presenting complaint', flex: 3 },
                        { label: 'Category', flex: 2 },
                        { label: 'Count', flex: 0.8, align: 'right' },
                      ]}
                      rows={Object.entries(c.complaints || {})
                        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
                        .map(([label, count]) => [label, complaintCategoryOf(label), count])}
                      emptyText="No presenting complaints recorded"
                    />
                  </>
                )}
              </>
            ) : (
              <Callout title="No patients">No patient presentations were recorded on this day.</Callout>
            )}
            <Columns>
              {showCad && d.cad?.total ? (
                <View>
                  <SectionTitle>Control room</SectionTitle>
                  <DayCad cad={d.cad} />
                </View>
              ) : null}
              {showTreatment && d.treatment?.patients ? (
                <View>
                  <SectionTitle>Treatment locations</SectionTitle>
                  <DataTable
                    columns={[{ label: 'Measure', flex: 2 }, { label: 'Value', flex: 1, align: 'right' }]}
                    rows={[
                      ['Patients admitted', d.treatment.patients],
                      ['Median length of stay', fmtMinutes(d.treatment.lengthOfStay.median)],
                      ['Peak at once (all locations)', `${d.treatment.peak.count}${d.treatment.peak.at ? ` at ${fmtDateTime(d.treatment.peak.at).slice(-5)}` : ''}`],
                    ]}
                  />
                </View>
              ) : null}
            </Columns>
            {(() => {
              const also = [
                showMeds && d.medicines ? `${d.medicines} medicine administrations` : null,
                showFeedback && (d.compliments || d.complaints)
                  ? `${d.compliments} patient compliments and ${d.complaints} complaints`
                  : null,
              ].filter(Boolean)
              return also.length ? (
                <Text style={{ fontSize: 7, color: MUTED, marginTop: 8 }}>{clean(`Also on this day: ${also.join(', ')}.`)}</Text>
              ) : null
            })()}
          </ReportPage>
        )
      })}
    </>
  )
}

export const NhsImpact = ({ number, data, chrome, include }) => {
  const c = data.overall.clinical
  const part = (key) => include.part('nhsImpact', key)
  const managedOnSite = c.total - c.conveyed - (c.outcomes['Not recorded'] || 0)
  return (
    <ReportPage chrome={chrome}>
      <SectionHeading
        number={number}
        title="NHS impact"
        intro="How much demand the event placed on local NHS services, and how much was kept away from them by treatment on site."
      />
      {part('headline') && (
        <StatRow
          items={[
            { label: 'Managed on site', value: Math.max(0, managedOnSite), sub: fmtPct(c.nonConveyanceRate), color: GOOD },
            { label: 'Went to hospital', value: c.conveyed, sub: fmtPct(c.conveyanceRate), color: c.conveyed ? WARN : GOOD },
            { label: 'Conveyed by us', value: c.outcomes['Conveyed to hospital by us'] || 0 },
            { label: 'NHS ambulance', value: c.nhsAmbulanceHandovers, sub: 'handed over to crew', color: c.nhsAmbulanceHandovers ? WARN : GOOD },
            { label: 'Own transport to A&E', value: c.outcomes['A&E by own transport'] || 0 },
          ]}
        />
      )}
      <InPairs
        blocks={[
          part('hospitals') && (
            <>
              <SectionTitle>Receiving hospitals</SectionTitle>
              <CountTable rows={topEntries(c.destinations, 10)} total={c.conveyed || undefined} emptyText="No patients went to hospital" />
            </>
          ),
          part('destinationTypes') && (
            <>
              <SectionTitle>Type of destination</SectionTitle>
              <CountTable rows={topEntries(c.destinationTypes, 10)} total={c.conveyed || undefined} emptyText="No patients went to hospital" />
            </>
          ),
        ]}
      />
      <Callout title="Reading these figures">
        {clean(
          '"Went to hospital" counts patients we conveyed ourselves, patients handed to an NHS ambulance crew, and patients advised to attend A&E by their own transport. "Managed on site" counts every other recorded outcome, including discharge after treatment, advice only and welfare support.',
        )}
      </Callout>
    </ReportPage>
  )
}

export const Medicines = ({ number, data, chrome }) => {
  const m = data.overall.medicines
  return (
    <ReportPage chrome={chrome}>
      <SectionHeading
        number={number}
        title="Medicines administered"
        intro="Administrations recorded on patient records linked to the event. Corrected and voided entries are excluded."
      />
      <StatRow
        items={[
          { label: 'Administrations', value: m.administrations },
          { label: 'Different medicines', value: m.byDrug.length },
          { label: 'Controlled drugs', value: m.controlled, sub: 'administrations', color: m.controlled ? WARN : GOOD },
        ]}
      />
      <DataTable
        columns={[
          { label: 'Medicine', flex: 4 },
          { label: 'Controlled', flex: 1 },
          { label: 'Administrations', flex: 1.2, align: 'right' },
        ]}
        rows={m.byDrug.map((d) => [d.title, d.controlled ? 'Yes' : '', d.count])}
      />
    </ReportPage>
  )
}

export const HealthSafety = ({ number, data, chrome }) => (
  <ReportPage chrome={chrome}>
    <SectionHeading
      number={number}
      title="Health and safety concerns"
      intro="Concerns our crews flagged for the event organiser while treating patients. Patient details are not included."
    />
    {data.healthSafety.length === 0 ? (
      <Callout title="No concerns raised" tone={GOOD}>
        No health and safety concerns were raised by our crews during the event.
      </Callout>
    ) : (
      data.healthSafety.map((h, i) => (
        <View
          key={i}
          style={{ borderWidth: 0.5, borderColor: LINE, borderRadius: 4, padding: 8, marginTop: 6 }}
        >
          <View
            style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}
            wrap={false}
            minPresenceAhead={30}
          >
            <Text style={{ fontSize: 8.5, fontFamily: 'Helvetica-Bold', color: NAVY }}>
              {clean(`Concern ${i + 1}${h.types.length ? ` - ${h.types.join(', ')}` : ''}`)}
            </Text>
            <Pill
              label={h.organiserNotified ? 'Organiser notified' : 'Organiser not notified'}
              color={h.organiserNotified ? GOOD : WARN}
            />
          </View>
          <Text style={{ fontSize: 6.5, color: MUTED, marginTop: 2 }}>
            {clean([fmtDateTime(h.ts), h.location].filter(Boolean).join(' - '))}
          </Text>
          {h.details ? (
            <Text style={{ fontSize: 7.5, color: INK, marginTop: 4, lineHeight: 1.4 }}>{clean(h.details)}</Text>
          ) : null}
          {h.immediateAction ? (
            <Text style={{ fontSize: 7, color: INK, marginTop: 3, lineHeight: 1.4 }}>
              <Text style={{ fontFamily: 'Helvetica-Bold' }}>Immediate action: </Text>
              {clean(h.immediateAction)}
            </Text>
          ) : null}
          {h.notesForOrganiser ? (
            <Text style={{ fontSize: 7, color: INK, marginTop: 3, lineHeight: 1.4 }}>
              <Text style={{ fontFamily: 'Helvetica-Bold' }}>Notes for the organiser: </Text>
              {clean(h.notesForOrganiser)}
            </Text>
          ) : null}
        </View>
      ))
    )}
  </ReportPage>
)
